/**
 * Formulier voor Nieuwe/Edit Relatie
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { relationsService, RelationCreate, RelatieType } from '../../services/relations.service';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import './Relations.css';

interface RelationFormProps {
  relationId?: number;
}

const RelationForm = ({ relationId }: RelationFormProps) => {
  const navigate = useNavigate();
  const isEditMode = !!relationId;

  const [formData, setFormData] = useState<RelationCreate>({
    relatienr: '',
    type: RelatieType.KLANT_PARTICULIER,
    aanhef: '',
    bedrijfsnaam: '',
    contactpersoon: '',
    contactpersoon_aanhef: '',
    contactpersoon_voornaam: '',
    contactpersoon_tussenvoegsel: '',
    contactpersoon_achternaam: '',
    voornaam: '',
    tussenvoegsel: '',
    achternaam: '',
    straat: '',
    huisnummer: '',
    postcode: '',
    plaats: '',
    land: 'Nederland',
    telefoon: '',
    mobiel: '',
    email: '',
    website: '',
    kvk_nummer: '',
    btw_nummer: '',
    iban: '',
    betalingstermijn: 30,
    is_zzp: false,
    notities: '',
    actief: true,
    klant_vanaf: '',
  });

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadRelation = useCallback(async () => {
    if (!relationId) return;

    try {
      setLoading(true);
      const relation = await relationsService.getById(relationId);
      setFormData(relation);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [relationId]);

  useEffect(() => {
    if (isEditMode) {
      loadRelation();
    }
  }, [isEditMode, loadRelation]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const inputType = (e.target as HTMLInputElement).type;

    setFormData((prev) => ({
      ...prev,
      [name]:
        inputType === 'number'
          ? value === ''
            ? undefined
            : parseFloat(value)
          : inputType === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEditMode && relationId) {
        await relationsService.update(relationId, formData);
        setSuccess('Relatie succesvol bijgewerkt!');
      } else {
        await relationsService.create(formData);
        setSuccess('Relatie succesvol aangemaakt!');
      }

      setTimeout(() => {
        navigate('/relations');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!formData.bedrijfsnaam || formData.bedrijfsnaam.trim().length < 2) {
      setError('Voer eerst een bedrijfsnaam in (minimaal 2 karakters)');
      return;
    }

    setSearching(true);
    setError(null);

    try {
      const result = await relationsService.searchCompany(formData.bedrijfsnaam.trim());

      // Vul de gevonden gegevens in
      setFormData(prev => ({
        ...prev,
        bedrijfsnaam: result.naam || prev.bedrijfsnaam,
        straat: result.straat || result.adres || prev.straat, // Backwards compatibility
        huisnummer: result.huisnummer || prev.huisnummer,
        postcode: result.postcode || prev.postcode,
        plaats: result.plaats || prev.plaats,
        email: result.email || prev.email,
        telefoon: result.telefoon || prev.telefoon,
        mobiel: result.mobiel || prev.mobiel,
        website: result.website || prev.website,
        btw_nummer: result.btw_nummer || prev.btw_nummer,
        kvk_nummer: result.kvk_nummer || prev.kvk_nummer,
      }));

      setSuccess('Bedrijfsgegevens gevonden en ingevuld!');
    } catch (err: any) {
      setError(err.message || 'Kon geen bedrijfsgegevens vinden');
    } finally {
      setSearching(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="relation-form">
      <div className="page-header">
        <h1>{isEditMode ? 'Relatie Bewerken' : 'Nieuwe Relatie'}</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit}>
        {/* Basis Informatie */}
        <section className="form-section">
          <h2>Basis Informatie</h2>
          <div className="form-grid">
            {/* Klant Particulier: Alles op één rij */}
            {formData.type === RelatieType.KLANT_PARTICULIER && (
              <div className="form-row">
                <div className="narrow-group">
                  <div className="form-group narrow">
                    <label htmlFor="type">
                      Type <span className="required">*</span>
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      required
                    >
                      <option value={RelatieType.KLANT_PARTICULIER}>{RelatieType.KLANT_PARTICULIER}</option>
                      <option value={RelatieType.KLANT_BEDRIJF}>{RelatieType.KLANT_BEDRIJF}</option>
                      <option value={RelatieType.LEVERANCIER}>{RelatieType.LEVERANCIER}</option>
                      <option value={RelatieType.BEIDE}>{RelatieType.BEIDE}</option>
                    </select>
                  </div>

                  <div className="form-group narrow">
                    <label htmlFor="aanhef">Aanhef</label>
                    <select
                      id="aanhef"
                      name="aanhef"
                      value={formData.aanhef}
                      onChange={handleChange}
                    >
                      <option value="">Selecteer...</option>
                      <option value="Dhr.">Dhr.</option>
                      <option value="Mevr.">Mevr.</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="voornaam">
                    Voornaam <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="voornaam"
                    name="voornaam"
                    value={formData.voornaam}
                    onChange={handleChange}
                    required
                    placeholder="Jan"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tussenvoegsel">Tussenvoegsel</label>
                  <input
                    type="text"
                    id="tussenvoegsel"
                    name="tussenvoegsel"
                    value={formData.tussenvoegsel}
                    onChange={handleChange}
                    placeholder="van, de, etc."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="achternaam">
                    Achternaam <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="achternaam"
                    name="achternaam"
                    value={formData.achternaam}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            {/* Klant Bedrijf/Leverancier/Beide: Alleen Type */}
            {(formData.type === RelatieType.KLANT_BEDRIJF || formData.type === RelatieType.LEVERANCIER || formData.type === RelatieType.BEIDE) && (
              <div className="form-group narrow">
                <label htmlFor="type">
                  Type <span className="required">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value={RelatieType.KLANT_PARTICULIER}>{RelatieType.KLANT_PARTICULIER}</option>
                  <option value={RelatieType.KLANT_BEDRIJF}>{RelatieType.KLANT_BEDRIJF}</option>
                  <option value={RelatieType.LEVERANCIER}>{RelatieType.LEVERANCIER}</option>
                  <option value={RelatieType.BEIDE}>{RelatieType.BEIDE}</option>
                </select>
              </div>
            )}

            {/* Klant Bedrijf, Leverancier en Beide: Bedrijfsnaam */}
            {(formData.type === RelatieType.KLANT_BEDRIJF || formData.type === RelatieType.LEVERANCIER || formData.type === RelatieType.BEIDE) && (
              <>
                <div className="form-group span-2">
                  <label htmlFor="bedrijfsnaam">
                    Bedrijfsnaam <span className="required">*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      id="bedrijfsnaam"
                      name="bedrijfsnaam"
                      value={formData.bedrijfsnaam}
                      onChange={handleChange}
                      required
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={handleSearch}
                      disabled={searching}
                      style={{
                        padding: '0.5rem 1rem',
                        background: searching ? '#ccc' : '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: searching ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      title="Zoek bedrijfsgegevens via AI"
                    >
                      {searching ? (
                        <>
                          <span style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            border: '2px solid #fff',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                          Zoeken...
                        </>
                      ) : (
                        <>Zoeken</>
                      )}
                    </button>
                  </div>
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                    Klik op Zoeken om bedrijfsgegevens automatisch op te halen via AI
                  </small>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Contactpersoon - Voor Klant Bedrijf, Leverancier en Beide */}
        {(formData.type === RelatieType.KLANT_BEDRIJF || formData.type === RelatieType.LEVERANCIER || formData.type === RelatieType.BEIDE) && (
          <section className="form-section">
            <h2>Contactpersoon</h2>
            <div className="form-row">
              <div className="form-group narrow">
                <label htmlFor="contactpersoon_aanhef">Aanhef</label>
                <select
                  id="contactpersoon_aanhef"
                  name="contactpersoon_aanhef"
                  value={formData.contactpersoon_aanhef}
                  onChange={handleChange}
                >
                  <option value="">Selecteer...</option>
                  <option value="Dhr.">Dhr.</option>
                  <option value="Mevr.">Mevr.</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="contactpersoon_voornaam">Voornaam</label>
                <input
                  type="text"
                  id="contactpersoon_voornaam"
                  name="contactpersoon_voornaam"
                  value={formData.contactpersoon_voornaam}
                  onChange={handleChange}
                  placeholder="Jan"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactpersoon_tussenvoegsel">Tussenvoegsel</label>
                <input
                  type="text"
                  id="contactpersoon_tussenvoegsel"
                  name="contactpersoon_tussenvoegsel"
                  value={formData.contactpersoon_tussenvoegsel}
                  onChange={handleChange}
                  placeholder="van, de, etc."
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactpersoon_achternaam">Achternaam</label>
                <input
                  type="text"
                  id="contactpersoon_achternaam"
                  name="contactpersoon_achternaam"
                  value={formData.contactpersoon_achternaam}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>
        )}

        {/* Adres Gegevens */}
        <section className="form-section">
          <h2>Adres Gegevens</h2>
          <div className="form-grid">
            <div className="form-group span-2">
              <label htmlFor="straat">Straat</label>
              <input
                type="text"
                id="straat"
                name="straat"
                value={formData.straat}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="huisnummer">Huisnummer</label>
              <input
                type="text"
                id="huisnummer"
                name="huisnummer"
                value={formData.huisnummer}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="postcode">Postcode</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="plaats">Plaats</label>
              <input
                type="text"
                id="plaats"
                name="plaats"
                value={formData.plaats}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="land">Land</label>
              <input
                type="text"
                id="land"
                name="land"
                value={formData.land}
                onChange={handleChange}
              />
            </div>
          </div>
        </section>

        {/* Contact Informatie */}
        <section className="form-section">
          <h2>Contact Informatie</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="telefoon">Telefoon</label>
              <input
                type="tel"
                id="telefoon"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobiel">Mobiel</label>
              <input
                type="tel"
                id="mobiel"
                name="mobiel"
                value={formData.mobiel}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {/* Website - niet voor een particulier. Een klant-bedrijf krijgt dit veld
                sinds 23-09-2026 wel; daarvoor was het net als bij een particulier
                verborgen, terwijl de detailpagina het wel toonde. */}
            {formData.type !== RelatieType.KLANT_PARTICULIER && (
              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        </section>

        {/* Bedrijfsgegevens - niet voor een particulier. Een klant-bedrijf heeft wel
            degelijk een KvK- en btw-nummer; dat laatste heb je nodig bij verlegde btw. */}
        {formData.type !== RelatieType.KLANT_PARTICULIER && (
          <section className="form-section">
            <h2>Bedrijfsgegevens</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="kvk_nummer">KvK Nummer</label>
                <input
                  type="text"
                  id="kvk_nummer"
                  name="kvk_nummer"
                  value={formData.kvk_nummer}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="btw_nummer">BTW Nummer</label>
                <input
                  type="text"
                  id="btw_nummer"
                  name="btw_nummer"
                  value={formData.btw_nummer}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="iban">IBAN</label>
                <input
                  type="text"
                  id="iban"
                  name="iban"
                  value={formData.iban}
                  onChange={handleChange}
                />
              </div>

              {/* ZZP veld alleen voor Leveranciers of Beide */}
              {(formData.type === RelatieType.LEVERANCIER || formData.type === RelatieType.BEIDE) && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_zzp"
                      checked={formData.is_zzp}
                      onChange={handleChange}
                    />
                    <span>ZZP'er (Zelfstandige zonder personeel)</span>
                  </label>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                    Vink aan indien deze leverancier een ZZP'er is voor urenregistratie
                  </small>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Financiële instellingen - Alleen voor Klant en Beide */}
        {(formData.type === RelatieType.KLANT_PARTICULIER || formData.type === RelatieType.KLANT_BEDRIJF || formData.type === RelatieType.BEIDE) && (
          <section className="form-section">
            <h2>Financiële Instellingen</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="betalingstermijn">Betalingstermijn (dagen)</label>
                <input
                  type="number"
                  id="betalingstermijn"
                  name="betalingstermijn"
                  value={formData.betalingstermijn}
                  onChange={handleChange}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="kredietlimiet">Kredietlimiet (€)</label>
                <input
                  type="number"
                  id="kredietlimiet"
                  name="kredietlimiet"
                  value={formData.kredietlimiet || ''}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </section>
        )}

        {/* Overige instellingen */}
        <section className="form-section">
          <h2>Overige Instellingen</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="relatienr">Relatienummer</label>
              <input
                type="text"
                id="relatienr"
                name="relatienr"
                value={formData.relatienr}
                onChange={handleChange}
                placeholder="Optioneel relatienummer"
              />
            </div>

            {/* Klant vanaf - Alleen voor Klant en Beide */}
            {(formData.type === RelatieType.KLANT_PARTICULIER || formData.type === RelatieType.KLANT_BEDRIJF || formData.type === RelatieType.BEIDE) && (
              <div className="form-group">
                <label htmlFor="klant_vanaf">Klant vanaf</label>
                <input
                  type="date"
                  id="klant_vanaf"
                  name="klant_vanaf"
                  value={formData.klant_vanaf}
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="actief"
                  checked={formData.actief}
                  onChange={handleChange}
                />
                <span>Actief</span>
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                Inactieve relaties worden niet getoond in overzichten
              </small>
            </div>
          </div>
        </section>

        {/* Notities */}
        <section className="form-section">
          <h2>Notities</h2>
          <div className="form-group">
            <textarea
              id="notities"
              name="notities"
              value={formData.notities}
              onChange={handleChange}
              rows={4}
              placeholder="Eventuele opmerkingen..."
            />
          </div>
        </section>

        <div className="form-actions">
          <button
            type="button"
            className="btn-lg btn-secondary"
            onClick={() => navigate('/relations')}
          >
            Annuleren
          </button>
          <button type="submit" className="btn-lg btn-success" disabled={loading}>
            {loading ? 'Bezig...' : isEditMode ? 'Bijwerken' : 'Aanmaken'}
          </button>
        </div>
      </form>

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default RelationForm;